import {
    createContext,
    createElement,
    useContext,
    useMemo,
    type FC,
    type PropsWithChildren,
} from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { camelCase } from "@common/helpers/string";
import { resolveIsCustomReportSettingsEnabled } from "@common/helpers/featureFlagResolvers";
import type {
    FeatureFlagOverridesResponse,
    WorkspaceFeatureFlagOverridesResponse,
} from "@common/schemas/featureFlagOverride.schema";
import {
    useFeatureFlagOverrides,
    useOrganizationFeatureFlagOverrides,
} from "@api/featureFlagOverride.api";
import { useDemoFeatureFlagScope } from "@hooks/useDemoFeatureFlagScope";
import { FeatureFlags } from "@lib/launchdarkly";

const camelizeKeys = (obj: Record<string, unknown>): FeatureFlags => {
    const result: FeatureFlags = Object.create(null);

    for (const [key, value] of Object.entries(obj)) {
        result[camelCase(key) as keyof FeatureFlags] = value as boolean;
    }

    return result;
};

// Kept in sync with the backend's per-request derivation.
const resolveDerivedFlags = (flags: FeatureFlags): FeatureFlags => ({
    ...flags,
    customReportSettings: resolveIsCustomReportSettingsEnabled({
        isReportTemplatesEnabled: flags.reportTemplates,
        isFlagEnabled: flags.customReportSettings,
    }),
});

/**
 * Reads a flag LaunchDarkly doesn't send as off instead of `undefined`, which
 * React Query's `enabled` would treat as on.
 */
const readMissingFlagsAsOff = (flags: FeatureFlags): FeatureFlags =>
    new Proxy(flags, {
        get: (target, key) => {
            const value = Reflect.get(target, key);
            return typeof key === "string" ? (value ?? false) : value;
        },
    });

/** Layers, last wins: LaunchDarkly or static flags, firm overrides, workspace overrides. */
export const resolveTypedFlags = ({
    baseFlags,
    organizationOverrides,
    overrides,
}: {
    baseFlags: FeatureFlags;
    organizationOverrides: Record<string, boolean>;
    overrides: Record<string, boolean>;
}): FeatureFlags =>
    readMissingFlagsAsOff(
        resolveDerivedFlags({
            ...baseFlags,
            ...camelizeKeys(organizationOverrides),
            ...camelizeKeys(overrides),
        }),
    );

export const getStaticFlagValues = (): Record<string, boolean> | null =>
    import.meta.env.VITE_LAUNCHDARKLY_FLAGS
        ? JSON.parse(import.meta.env.VITE_LAUNCHDARKLY_FLAGS).flagValues
        : null;

const EMPTY_OVERRIDES: Record<string, boolean> = {};

/** Picks the workspace and firm override layers for the active demo scope. */
export const selectDemoOverrideLayers = ({
    workspaceResponse,
    organizationResponse,
}: {
    workspaceResponse: WorkspaceFeatureFlagOverridesResponse | undefined;
    organizationResponse: FeatureFlagOverridesResponse | undefined;
}) => ({
    overrides: workspaceResponse?.overrides ?? EMPTY_OVERRIDES,
    organizationOverrides:
        workspaceResponse?.organizationOverrides ??
        organizationResponse?.overrides ??
        EMPTY_OVERRIDES,
});

const TypedFlagsContext = createContext<FeatureFlags | null>(null);

/** Merge once per LD update, not per consumer render. */
export const TypedFlagsProvider: FC<PropsWithChildren> = ({ children }) => {
    const launchDarklyFlags = useFlags<FeatureFlags>();
    const demoScope = useDemoFeatureFlagScope();
    const workspaceId =
        demoScope?.kind === "workspace" ? demoScope.workspaceId : null;
    const organizationId =
        demoScope?.kind === "organization" ? demoScope.organizationId : null;
    const { data: workspaceResponse } = useFeatureFlagOverrides(workspaceId, {
        enabled: workspaceId !== null,
    });
    const { data: organizationResponse } = useOrganizationFeatureFlagOverrides(
        organizationId,
        { enabled: organizationId !== null },
    );
    const { overrides, organizationOverrides } = selectDemoOverrideLayers({
        workspaceResponse,
        organizationResponse,
    });

    const transformedStaticFlags = useMemo(() => {
        const staticFlagValues = getStaticFlagValues();
        return staticFlagValues ? camelizeKeys(staticFlagValues) : null;
    }, []);

    const flags = useMemo(
        () =>
            resolveTypedFlags({
                baseFlags: transformedStaticFlags ?? launchDarklyFlags,
                organizationOverrides,
                overrides,
            }),
        [
            launchDarklyFlags,
            transformedStaticFlags,
            organizationOverrides,
            overrides,
        ],
    );

    return createElement(
        TypedFlagsContext.Provider,
        { value: flags },
        children,
    );
};

export function useTypedFlags(): FeatureFlags {
    const flags = useContext(TypedFlagsContext);
    if (!flags) {
        throw new Error("useTypedFlags must be used within TypedFlagsProvider");
    }
    return flags;
}
